package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.api.HomeSectionApiClient
import com.castly.admin.dto.ContentResponse
import com.castly.admin.dto.EpisodeResponse
import com.castly.admin.dto.TagResponse
import com.castly.admin.external.Sortable
import com.castly.admin.external.SortableInstance
import com.castly.admin.external.createSortableOptions
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import com.castly.shared.dto.SectionTranslationData
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/**
 * Carousel Editor Component (T063-T070)
 *
 * Features:
 * - T063: Bilingual input fields (English/Arabic title and description)
 * - T064: Search input with debounced API call to search podcasts/audiobooks
 * - T065: Add by ID input with UUID validation
 * - T066: Tag selector dropdown with "Add all items with this tag" button
 * - T068: Item list display showing current carousel items with remove buttons
 * - T069: Language toggle (English/Arabic) to preview translations
 * - Carousels support mixed content (podcasts and audiobooks together)
 * - episodeMode: items are hand-picked episodes (search a title, then pick its episodes); no tag tab
 */
@Composable
fun CarouselEditor(
    section: AdminSectionDto?,
    apiClient: ApiClient,
    homeSectionApiClient: HomeSectionApiClient,
    episodeMode: Boolean = false,
    onSave: (AdminSectionDto) -> Unit = {},
    onDataChanged: (String, String?, List<SectionTranslationData>) -> Unit = { _, _, _ -> }
) {
    val colors = AppTheme.colors

    // State for translations
    var titleEn by remember { mutableStateOf(section?.translations?.find { it.languageCode == "eng" }?.title ?: "") }
    var titleAr by remember { mutableStateOf(section?.translations?.find { it.languageCode == "ara" }?.title ?: "") }
    var descEn by remember { mutableStateOf(section?.translations?.find { it.languageCode == "eng" }?.description ?: "") }
    var descAr by remember { mutableStateOf(section?.translations?.find { it.languageCode == "ara" }?.description ?: "") }
    var internalName by remember { mutableStateOf(section?.internalName ?: "") }

    val isEditMode = section != null

    // T064: Search state
    var searchQuery by remember { mutableStateOf("") }
    var searchResults by remember { mutableStateOf<List<ContentResponse>>(emptyList()) }
    var searchLoading by remember { mutableStateOf(false) }
    var searchDebounceJob by remember { mutableStateOf<Job?>(null) }

    // Episode mode: the title picked from search and its published episodes
    var pickedContent by remember { mutableStateOf<ContentResponse?>(null) }
    var pickedEpisodes by remember { mutableStateOf<List<EpisodeResponse>>(emptyList()) }
    var episodesLoading by remember { mutableStateOf(false) }

    // T065: Add by ID state
    var addByIdValue by remember { mutableStateOf("") }
    var addByIdError by remember { mutableStateOf<String?>(null) }

    // T066: Tag selector state
    var tags by remember { mutableStateOf<List<TagResponse>>(emptyList()) }
    var selectedTagId by remember { mutableStateOf<String?>(null) }
    var tagsLoading by remember { mutableStateOf(false) }

    // T068: Current carousel items
    var carouselItems by remember { mutableStateOf<List<ContentResponse>>(emptyList()) }
    var itemsLoading by remember { mutableStateOf(false) }

    // Add items tab state
    var addTab by remember { mutableStateOf("search") } // "search" | "id" | "tag"

    // General state
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }

    // Confirm dialog state for item removal
    var confirmRemoveItem by remember { mutableStateOf<ContentResponse?>(null) }

    // SortableJS container ref and revert snapshot for drag-and-drop item ordering
    var itemListContainer by remember { mutableStateOf<HTMLElement?>(null) }

    val scope = rememberCoroutineScope()

    // Load tags on mount
    LaunchedEffect(Unit) {
        tagsLoading = true
        try {
            val result = apiClient.getTags("en")
            when (result) {
                is ApiClient.ApiResult.Success -> tags = result.data
                is ApiClient.ApiResult.Error -> error = "Failed to load tags: ${result.message}"
            }
        } catch (e: Exception) {
            error = "Failed to load tags: ${e.message}"
        } finally {
            tagsLoading = false
        }
    }

    // Load carousel items if editing existing section
    LaunchedEffect(section?.id) {
        section?.id?.let { sectionId ->
            itemsLoading = true
            try {
                carouselItems = homeSectionApiClient.getSectionItems(sectionId)
            } catch (e: Exception) {
                error = "Failed to load carousel items: ${e.message}"
                console.error("Error loading carousel items", e)
            } finally {
                itemsLoading = false
            }
        }
    }

    // SortableJS drag-and-drop for carousel item reordering
    DisposableEffect(itemListContainer) {
        var sortableInstance: SortableInstance? = null
        val container = itemListContainer

        if (container != null) {
            val options = createSortableOptions {
                animation = 150
                delay = 100
                delayOnTouchOnly = true
                handle = ".drag-handle"
                ghostClass = "sortable-ghost"
                dragClass = "sortable-drag"

                // NOTE: Do NOT update Compose state here — SortableJS already moved DOM elements.
                // Updating state triggers recomposition that conflicts with SortableJS's DOM changes.
                onEnd = { _ ->
                    val elements = container.querySelectorAll(".carousel-item-row")
                    val newItemIds = (0 until elements.length).mapNotNull { i ->
                        (elements.item(i) as? org.w3c.dom.Element)?.getAttribute("data-item-id")
                    }
                    if (newItemIds.isNotEmpty() && section?.id != null) {
                        val sectionId = section.id
                        scope.launch {
                            try {
                                homeSectionApiClient.reorderItems(sectionId, newItemIds)
                                success = "Order saved"
                            } catch (e: Exception) {
                                // Reload from server on failure to revert visual
                                try {
                                    carouselItems = homeSectionApiClient.getSectionItems(sectionId)
                                } catch (_: Exception) {}
                                error = "Failed to save order: ${e.message}"
                            }
                        }
                    }
                }
            }
            sortableInstance = Sortable.create(container, options)
        }

        onDispose { sortableInstance?.destroy() }
    }

    // Notify parent when form data changes
    LaunchedEffect(internalName, titleEn, titleAr, descEn, descAr) {
        val translations = buildList {
            if (titleEn.isNotBlank()) {
                add(SectionTranslationData("eng", titleEn, descEn.takeIf { it.isNotBlank() }))
            }
            if (titleAr.isNotBlank()) {
                add(SectionTranslationData("ara", titleAr, descAr.takeIf { it.isNotBlank() }))
            }
        }
        onDataChanged(internalName, null, translations)
    }

    // T064: Debounced search function
    fun performSearch(query: String) {
        searchDebounceJob?.cancel()
        if (query.length < 2) {
            searchResults = emptyList()
            return
        }

        searchDebounceJob = scope.launch {
            delay(500) // Debounce delay
            searchLoading = true
            try {
                val result = apiClient.searchContent(
                    query = query,
                    page = 1,
                    pageSize = 10
                )
                when (result) {
                    is ApiClient.ApiResult.Success -> {
                        searchResults = result.data.items
                    }
                    is ApiClient.ApiResult.Error -> error = "Search failed: ${result.message}"
                }
            } catch (e: Exception) {
                error = "Search failed: ${e.message}"
            } finally {
                searchLoading = false
            }
        }
    }

    // T064: Add item from search
    fun addItemFromSearch(item: ContentResponse) {
        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    homeSectionApiClient.addItemById(sectionId, item.id)
                    success = "Item added successfully"
                    searchResults = emptyList()
                    searchQuery = ""
                    // Reload items to get updated list
                    carouselItems = homeSectionApiClient.getSectionItems(sectionId)
                } catch (e: Exception) {
                    error = "Failed to add item: ${e.message}"
                }
            }
        }
    }

    // Episode mode: load all published episodes of the picked title
    fun pickContent(item: ContentResponse) {
        pickedContent = item
        pickedEpisodes = emptyList()
        searchResults = emptyList()
        searchQuery = ""
        scope.launch {
            episodesLoading = true
            try {
                val pageSize = 50
                val all = mutableListOf<EpisodeResponse>()
                var page = 1
                while (true) {
                    when (val result = apiClient.getContentEpisodes(item.id, page = page, pageSize = pageSize)) {
                        is ApiClient.ApiResult.Success -> {
                            all.addAll(result.data)
                            if (result.data.size < pageSize) break
                            page++
                        }
                        is ApiClient.ApiResult.Error -> {
                            error = "Failed to load episodes: ${result.message}"
                            break
                        }
                    }
                }
                pickedEpisodes = all
            } catch (e: Exception) {
                error = "Failed to load episodes: ${e.message}"
            } finally {
                episodesLoading = false
            }
        }
    }

    // Episode mode: add one episode of the picked title
    fun addEpisode(episode: EpisodeResponse) {
        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    homeSectionApiClient.addItemById(sectionId, episode.id)
                    success = "Episode added successfully"
                    carouselItems = homeSectionApiClient.getSectionItems(sectionId)
                } catch (e: Exception) {
                    error = "Failed to add episode: ${e.message}"
                }
            }
        }
    }

    // T065: Add item by ID with UUID validation
    fun addItemById() {
        addByIdError = null

        // UUID validation
        val uuidRegex = Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$")
        if (!uuidRegex.matches(addByIdValue)) {
            addByIdError = "Invalid UUID format"
            return
        }

        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    homeSectionApiClient.addItemById(sectionId, addByIdValue)
                    success = "Item added successfully"
                    addByIdValue = ""
                    // Reload items to get updated list
                    carouselItems = homeSectionApiClient.getSectionItems(sectionId)
                } catch (e: Exception) {
                    addByIdError = e.message ?: "Failed to add item"
                }
            }
        }
    }

    // T066: Add items by tag
    fun addItemsByTag(tagId: String) {
        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    val addedIds = homeSectionApiClient.addItemsByTag(sectionId, tagId)
                    success = "Added ${addedIds.size} items from tag"
                    // Reload items to get updated list
                    carouselItems = homeSectionApiClient.getSectionItems(sectionId)
                } catch (e: Exception) {
                    error = "Failed to add items by tag: ${e.message}"
                }
            }
        }
    }

    // T068: Remove item
    fun removeItem(itemId: String) {
        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    homeSectionApiClient.removeItem(sectionId, itemId)
                    success = "Item removed successfully"
                    carouselItems = carouselItems.filter { it.id != itemId }
                } catch (e: Exception) {
                    error = "Failed to remove item: ${e.message}"
                }
            }
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {
        H3({ style { marginTop(0.px); marginBottom(Spacing.xl); color(Color(colors.text)) } }) {
            Text(if (episodeMode) "Episode Carousel Section Editor" else "Carousel Section Editor")
        }

        // Error/Success messages
        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Text(msg)
                Button({
                    onClick { error = null }
                    style {
                        marginLeft(Spacing.sm)
                        padding(Spacing.xs, Spacing.sm)
                        backgroundColor(Color.transparent)
                        border(0.px)
                        cursor("pointer")
                        color(Color(colors.text))
                    }
                }) {
                    Text("✕")
                }
            }
        }

        success?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#d1fae5" else "#052e16"))
                    color(Color(if (colors == LightColors) "#065f46" else "#86efac"))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Text(msg)
                Button({
                    onClick { success = null }
                    style {
                        marginLeft(Spacing.sm)
                        padding(Spacing.xs, Spacing.sm)
                        backgroundColor(Color.transparent)
                        border(0.px)
                        cursor("pointer")
                        color(Color(colors.text))
                    }
                }) {
                    Text("✕")
                }
            }
        }

        // Internal name
        Div({ style { marginBottom(Spacing.lg) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                Text("Internal Name")
            }
            Input(InputType.Text) {
                attr("type", "text")
                attr("value", internalName)
                attr("placeholder", "e.g., Featured Podcasts")
                onInput { internalName = it.value }
                style {
                    width(100.percent)
                    padding(Spacing.sm)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                }
            }
        }

        // T063: Bilingual input fields side by side
        // Titles row
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "${Spacing.lg}")
                marginBottom(Spacing.lg)
            }
        }) {
            Div({ style { flex("1") } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("English Title")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", titleEn)
                    attr("placeholder", "Featured Content")
                    onInput { titleEn = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
            Div({ style { flex("1") } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Arabic Title (العربية)")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", titleAr)
                    attr("placeholder", "محتوى مميز")
                    attr("dir", "rtl")
                    onInput { titleAr = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
        }

        // Descriptions row
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "${Spacing.lg}")
                marginBottom(Spacing.lg)
            }
        }) {
            Div({ style { flex("1") } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("English Description (Optional)")
                }
                TextArea {
                    value(descEn)
                    onInput { descEn = it.value }
                    attr("placeholder", "Browse our featured podcasts...")
                    attr("rows", "3")
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
            Div({ style { flex("1") } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Arabic Description (Optional)")
                }
                TextArea {
                    value(descAr)
                    onInput { descAr = it.value }
                    attr("placeholder", "تصفح البودكاست المميزة...")
                    attr("rows", "3")
                    attr("dir", "rtl")
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
        }

        // Section divider
        Hr { style { margin(Spacing.xl, 0.px); property("border-color", colors.border) } }

        H4({ style { color(Color(colors.text)) } }) { Text("Carousel Items") }

        // Show message for new sections
        if (section?.id == null) {
            Div({
                style {
                    padding(Spacing.lg)
                    backgroundColor(Color(if (colors == LightColors) "#fff3cd" else "#451a03"))
                    color(Color(if (colors == LightColors) "#856404" else "#fcd34d"))
                    borderRadius(BorderRadius.sm)
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#ffeaa7" else "#78350f"))
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Div({ style { fontWeight("bold"); marginBottom(Spacing.sm) } }) {
                    Text("Save the section first")
                }
                Div({ style { fontSize(Typography.base) } }) {
                    Text("You can add items to the carousel after creating the section. Click 'Save Section' below, then edit the section to add items.")
                }
            }
        }

        // Only show item management if section exists (editing mode)
        section?.id?.let {
            // Tab bar for adding items
            Div({
                style {
                    display(DisplayStyle.Flex)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    marginBottom(0.px)
                    property("overflow", "hidden")
                }
            }) {
                val addTabs = if (episodeMode) {
                    listOf("search" to "Pick Episode", "id" to "By Episode ID")
                } else {
                    listOf("search" to "Search", "id" to "By ID", "tag" to "By Tag")
                }
                addTabs.forEach { (key, label) ->
                    Button({
                        onClick { addTab = key }
                        style {
                            flex("1")
                            padding(Spacing.sm, Spacing.md)
                            border(0.px)
                            property("border-right", if (key != addTabs.last().first) "1px solid ${colors.border}" else "none")
                            backgroundColor(if (addTab == key) Color(colors.primary) else Color(colors.surfaceHover))
                            color(if (addTab == key) Color("#ffffff") else Color(colors.text))
                            cursor("pointer")
                            fontWeight(if (addTab == key) "bold" else "normal")
                        }
                    }) {
                        Text(label)
                    }
                }
            }

            // Tab panel
            Div({
                style {
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    property("border-top", "none")
                    borderRadius(0.px, 0.px, BorderRadius.sm, BorderRadius.sm)
                    padding(Spacing.lg)
                    marginBottom(Spacing.lg)
                }
            }) {
                when (addTab) {
                    "search" -> {
                        // T064: Search input
                        Input(InputType.Text) {
                            attr("type", "text")
                            attr("value", searchQuery)
                            attr("placeholder", if (episodeMode) "Search for a book or podcast..." else "Search by title...")
                            onInput {
                                searchQuery = it.value
                                performSearch(it.value)
                            }
                            style {
                                width(100.percent)
                                padding(Spacing.sm)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.sm)
                                backgroundColor(Color(colors.surface))
                                color(Color(colors.text))
                                property("box-sizing", "border-box")
                            }
                        }
                        if (searchLoading) {
                            Div({ style { marginTop(Spacing.sm); color(Color(colors.textMuted)) } }) {
                                Text("Searching...")
                            }
                        }
                        if (searchResults.isNotEmpty()) {
                            Div({
                                style {
                                    marginTop(Spacing.sm)
                                    maxHeight(200.px)
                                    property("overflow-y", "auto")
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.sm)
                                }
                            }) {
                                searchResults.forEach { item ->
                                    Div({
                                        onClick { if (episodeMode) pickContent(item) else addItemFromSearch(item) }
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            property("gap", "${Spacing.sm}")
                                            padding(Spacing.sm)
                                            cursor("pointer")
                                            color(Color(colors.text))
                                        }
                                    }) {
                                        Text(item.title)
                                        Span({
                                            style {
                                                fontSize(Typography.xs)
                                                padding(2.px, 6.px)
                                                borderRadius(10.px)
                                                backgroundColor(if (item.type == "audiobook") Color(if (colors == LightColors) "#e3f2fd" else "#0c2d48") else Color(if (colors == LightColors) "#e8f5e9" else "#0d3320"))
                                                color(if (item.type == "audiobook") Color(if (colors == LightColors) "#1565c0" else "#90caf9") else Color(if (colors == LightColors) "#2e7d32" else "#a5d6a7"))
                                            }
                                        }) {
                                            Text(item.type)
                                        }
                                    }
                                }
                            }
                        }
                        if (episodeMode) {
                            pickedContent?.let { content ->
                                Div({ style { marginTop(Spacing.md); fontWeight("bold"); color(Color(colors.text)) } }) {
                                    Text(content.title)
                                }
                                if (episodesLoading) {
                                    Div({ style { marginTop(Spacing.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Loading episodes...")
                                    }
                                } else if (pickedEpisodes.isEmpty()) {
                                    Div({ style { marginTop(Spacing.sm); color(Color(colors.textMuted)) } }) {
                                        Text("No published episodes.")
                                    }
                                } else {
                                    Div({
                                        style {
                                            marginTop(Spacing.sm)
                                            maxHeight(260.px)
                                            property("overflow-y", "auto")
                                            border(1.px, LineStyle.Solid, Color(colors.border))
                                            borderRadius(BorderRadius.sm)
                                        }
                                    }) {
                                        pickedEpisodes.forEach { episode ->
                                            val alreadyAdded = carouselItems.any { it.id == episode.id }
                                            Div({
                                                style {
                                                    display(DisplayStyle.Flex)
                                                    justifyContent(JustifyContent.SpaceBetween)
                                                    alignItems(AlignItems.Center)
                                                    property("gap", "${Spacing.sm}")
                                                    padding(Spacing.sm)
                                                    color(Color(colors.text))
                                                }
                                            }) {
                                                Span { Text("${episode.episodeNumber}. ${episode.title}") }
                                                Button({
                                                    if (alreadyAdded) attr("disabled", "true")
                                                    onClick { addEpisode(episode) }
                                                    style {
                                                        padding(Spacing.xs, Spacing.md)
                                                        backgroundColor(if (alreadyAdded) Color(colors.surfaceHover) else Color(colors.success))
                                                        color(if (alreadyAdded) Color(colors.textMuted) else Color("#ffffff"))
                                                        border(0.px)
                                                        borderRadius(BorderRadius.sm)
                                                        cursor(if (alreadyAdded) "default" else "pointer")
                                                    }
                                                }) {
                                                    Text(if (alreadyAdded) "Added" else "Add")
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    "id" -> {
                        // T065: Add by ID
                        Div({ style { display(DisplayStyle.Flex); property("gap", "${Spacing.sm}") } }) {
                            Input(InputType.Text) {
                                attr("type", "text")
                                attr("value", addByIdValue)
                                attr("placeholder", "UUID (e.g., 123e4567-e89b-12d3-a456-426614174000)")
                                onInput { addByIdValue = it.value }
                                style {
                                    flex("1")
                                    padding(Spacing.sm)
                                    border(1.px, LineStyle.Solid, if (addByIdError != null) Color(colors.danger) else Color(colors.border))
                                    borderRadius(BorderRadius.sm)
                                    backgroundColor(Color(colors.surface))
                                    color(Color(colors.text))
                                }
                            }
                            Button({
                                onClick { addItemById() }
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    backgroundColor(Color(colors.success))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.sm)
                                    cursor("pointer")
                                }
                            }) {
                                Text("Add")
                            }
                        }
                        addByIdError?.let { err ->
                            Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginTop(Spacing.xs) } }) {
                                Text(err)
                            }
                        }
                    }
                    "tag" -> {
                        // T066: Tag selector
                        Div({ style { display(DisplayStyle.Flex); property("gap", "${Spacing.sm}") } }) {
                            Select({
                                onChange { event -> selectedTagId = event.value }
                                style {
                                    flex("1")
                                    padding(Spacing.sm)
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.sm)
                                    backgroundColor(Color(colors.surface))
                                    color(Color(colors.text))
                                }
                            }) {
                                Option("") { Text("-- Select a tag --") }
                                tags.forEach { tag ->
                                    Option(tag.id) { Text(tag.name) }
                                }
                            }
                            Button({
                                onClick {
                                    selectedTagId?.let { tagId ->
                                        if (tagId.isNotEmpty()) addItemsByTag(tagId)
                                    }
                                }
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    backgroundColor(Color(colors.primary))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.sm)
                                    cursor("pointer")
                                }
                            }) {
                                Text("Add All")
                            }
                        }
                        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                            Text("Static snapshot: new items tagged later won't auto-update")
                        }
                    }
                }
            }

            // T068: Item list display
            Hr { style { margin(Spacing.xl, 0.px); property("border-color", colors.border) } }

            H4({ style { color(Color(colors.text)) } }) { Text("Current Items (${carouselItems.size})") }

            if (itemsLoading) {
                Div({ style { textAlign("center"); padding(Spacing.xl); color(Color(colors.textMuted)) } }) {
                    Text("Loading items...")
                }
            } else if (carouselItems.isEmpty()) {
                Div({ style { textAlign("center"); padding(Spacing.xl); color(Color(colors.textMuted)) } }) {
                    Text(if (episodeMode) "No episodes yet. Pick a title and add its episodes." else "No items yet. Use search, ID, or tag to add items.")
                }
            } else {
                Div({
                    style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("gap", "${Spacing.sm}") }
                    ref { element -> itemListContainer = element; onDispose { itemListContainer = null } }
                }) {
                    carouselItems.forEach { item ->
                        Div({
                            attr("data-item-id", item.id)
                            classes("carousel-item-row")
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                padding(Spacing.md)
                                backgroundColor(Color(colors.surfaceHover))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.sm)
                                themeTransition()
                            }
                        }) {
                            // Drag handle
                            Span({
                                classes("drag-handle")
                                style {
                                    cursor("grab")
                                    padding(Spacing.xs)
                                    paddingRight(Spacing.sm)
                                    color(Color(colors.textMuted))
                                    fontSize(Typography.xl)
                                    property("user-select", "none")
                                    property("line-height", "1")
                                }
                            }) {
                                Text("⠿")
                            }
                            Div({ style { flex("1") } }) {
                                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "${Spacing.sm}") } }) {
                                    Div({ style { fontWeight("bold"); color(Color(colors.text)) } }) {
                                        Text(item.title)
                                    }
                                    Span({
                                        style {
                                            fontSize(Typography.xs)
                                            padding(2.px, 6.px)
                                            borderRadius(10.px)
                                            backgroundColor(if (item.type == "audiobook") Color(if (colors == LightColors) "#e3f2fd" else "#0c2d48") else Color(if (colors == LightColors) "#e8f5e9" else "#0d3320"))
                                            color(if (item.type == "audiobook") Color(if (colors == LightColors) "#1565c0" else "#90caf9") else Color(if (colors == LightColors) "#2e7d32" else "#a5d6a7"))
                                        }
                                    }) {
                                        Text(item.type)
                                    }
                                }
                                Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                    Text("ID: ${item.id}")
                                }
                            }
                            Button({
                                onClick {
                                    confirmRemoveItem = item
                                }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(Color(colors.danger))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.sm)
                                    cursor("pointer")
                                }
                            }) {
                                Text("Remove")
                            }
                        }
                    }
                }
            }
        }
    }

    ConfirmDialog(
        isOpen = confirmRemoveItem != null,
        title = "Remove Item",
        message = "Remove this item from carousel?",
        confirmLabel = "Remove",
        onConfirm = { confirmRemoveItem?.let { removeItem(it.id) }; confirmRemoveItem = null },
        onCancel = { confirmRemoveItem = null }
    )
}
